import { FC, useMemo, useState } from 'react';
import { AllieChip, AllieDatetime, AllieModal, AllieText } from '../../../components';
import {
  FlexibleContainer,
  HalfShiftSelectorContainer,
  StyledCalendarButtonText,
  StyledCard,
  StyledIconButton,
  StyledMobileModal,
  StyledMobileModalContent,
  StyledMobileModalContentContainer,
  StyledPopover,
  StyledSection,
  StyledShiftNameText,
} from './ShiftSelector.styles';
import {
  AllieCalendarIcon,
  AllieChevronBackOutline,
  AllieChevronForward,
  AllieFlipBackwardIcon,
  AllieLayersOutlineIcon,
} from '../../../styling/icons';
import { formatDate, isMobile } from '../../../common/utils';
import { DataValueStatistics } from '../DataValueStatistics';
import { ShiftSelectorProps } from './ShiftSelector.types';
import { OrderStatus } from '../ProductionOrder/components/ToolbarButtons';
import { AllieChipStyled, StyledDescText } from '../ProductionOrder/ProductionOrder.styles';

export const ShiftSelector: FC<ShiftSelectorProps> = ({
  labelShift,
  labelDoneText,
  labelCancelText,
  labelGoToCurrent,
  currentShiftId,
  selectedDate,
  isCurrentShift,
  maxSelectedDate,
  onClickBackShift,
  onChangeDate,
  onClickForwardShift,
  onClickCurrentChip,
  timezone,
  showDate = false,
  shiftProduced,
  labelToday,
  lang,
  full = false,
  isProductive,
  labelNonWorkable,
}) => {
  const minDate = useMemo(() => {
    const currentDate = new Date();
    currentDate.setFullYear(currentDate.getFullYear() - 1);
    return currentDate.toISOString();
  }, []);
  const [mobileCalendarOpen, setMobileCalendarOpen] = useState(false);

  const hideBackShift = useMemo(() => {
    const minDateFormatted = new Date(minDate);
    minDateFormatted.setHours(0, 0, 0, 0);
    const selectedDateFormatted = new Date(selectedDate);
    selectedDateFormatted.setHours(0, 0, 0, 0);
    return minDateFormatted.getTime() === selectedDateFormatted.getTime() && currentShiftId === 1;
  }, [currentShiftId, minDate, selectedDate]);

  return (
    <>
      <StyledCard
        content={{
          children: (
            <StyledSection isMobile={isMobile}>
              <FlexibleContainer full={full}>
                <HalfShiftSelectorContainer>
                  {!hideBackShift && (
                    <StyledIconButton
                      iconOnly
                      allieSize="small"
                      allieType="secondary"
                      corner="rounded-edges"
                      onclick={onClickBackShift}
                      id="button-back-shift"
                      style={{ height: '20px' }}
                    >
                      <AllieChevronBackOutline
                        color="neutral-60"
                        style={{ width: '20px', height: '20px' }}
                      />
                    </StyledIconButton>
                  )}
                  <StyledIconButton
                    iconOnly={!showDate}
                    allieSize="small"
                    allieType="secondary"
                    corner="rounded-edges-plus"
                    id={isMobile ? '' : 'calendar-button'}
                    onClick={() => isMobile && setMobileCalendarOpen(true)}
                    style={{ padding: '6px 8px', borderRadius: '8px' }}
                  >
                    {showDate ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                        <AllieCalendarIcon
                          color="neutral-60"
                          style={{ width: '20px', height: '20px' }}
                        />
                        <StyledCalendarButtonText type="body3" weight={500} color="neutral-60">
                          {formatDate(selectedDate, timezone, 'DD/MM/YYYY')}
                        </StyledCalendarButtonText>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <AllieCalendarIcon
                          color="neutral-60"
                          style={{ width: '20px', height: '20px' }}
                        />
                        <StyledCalendarButtonText type="body3" weight={500} color="neutral-60">
                          {labelToday}
                        </StyledCalendarButtonText>
                      </div>
                    )}
                  </StyledIconButton>
                </HalfShiftSelectorContainer>
                <StyledPopover
                  triggerAction="click"
                  trigger="calendar-button"
                  size="auto"
                  arrow={true}
                  mode="ios"
                  content={{
                    children: (
                      <div
                        className="ion-padding"
                        style={{ display: 'flex', justifyContent: 'center' }}
                      >
                        <AllieDatetime
                          presentation="date"
                          mode="md"
                          onChange={(e) => {
                            onChangeDate(e.detail.value);
                          }}
                          showDefaultButtons
                          locale={lang || navigator.language}
                          doneText={labelDoneText}
                          cancelText={labelCancelText}
                          max={maxSelectedDate}
                          min={minDate}
                        ></AllieDatetime>
                      </div>
                    ),
                  }}
                />
                <HalfShiftSelectorContainer style={{ width: full ? '100%' : 'unset' }}>
                  <div style={{ ...(full ? { flex: 1, textAlign: 'center' } : {}) }}>
                    <StyledShiftNameText type="body1" style={{ lineHeight: '24px' }}>
                      {labelShift}
                    </StyledShiftNameText>
                  </div>

                  {!isProductive && labelNonWorkable && (
                    <AllieChipStyled status='done' style={{ flexShrink: 0, margin: 0 }}>
                      <StyledDescText
                        type="body2"
                        color='neutral-70'
                        weight={500}
                      >
                        {labelNonWorkable}
                      </StyledDescText>
                    </AllieChipStyled>
                  )}

                  <StyledIconButton
                    iconOnly
                    allieSize="small"
                    allieType="secondary"
                    corner="rounded-edges"
                    onclick={onClickForwardShift}
                    disabled={isCurrentShift}
                    style={{ height: '20px' }}
                  >
                    <AllieChevronForward
                      color="neutral-60"
                      style={{ width: '20px', height: '20px' }}
                    />
                  </StyledIconButton>

                  {shiftProduced && (
                    <DataValueStatistics
                      title={{ label: shiftProduced.label, type: 'h4' }}
                      value={shiftProduced.produced}
                      icon={AllieLayersOutlineIcon}
                      labelWidth={100}
                      style={{ marginLeft: '16px' }}
                    />
                  )}
                </HalfShiftSelectorContainer>
              </FlexibleContainer>
              {!isCurrentShift && (
                <StyledIconButton
                  onclick={onClickCurrentChip}
                  allieSize={isMobile ? 'medium' : 'small'}
                  allieType="secondary"
                  corner="rounded-edges-plus"
                  full={isMobile}
                  style={{
                    padding: isMobile ? '10px 8px' : '6px 8px',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    height: isMobile ? '40px' : 'unset',
                  }}
                >
                  <div style={{ display: 'flex', height: '20px', gap: '6px' }}>
                    <AllieFlipBackwardIcon style={{ width: '20px', height: '20px' }} />
                    <AllieText type="body3" weight={500} color="neutral-60">
                      {labelGoToCurrent}
                    </AllieText>
                  </div>
                </StyledIconButton>
              )}
            </StyledSection>
          ),
        }}
      />

      <StyledMobileModal
        isOpen={mobileCalendarOpen}
        onDidDismiss={async () => {
          setMobileCalendarOpen(false);
          return Promise.resolve({});
        }}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
        isMobile={isMobile}
      >
        <StyledMobileModalContentContainer>
          <StyledMobileModalContent className="ion-padding">
            <AllieDatetime
              presentation="date"
              mode="md"
              onChange={(e) => {
                onChangeDate(e.detail.value);
              }}
              locale={lang || navigator.language}
              doneText={labelDoneText}
              cancelText={labelCancelText}
              max={maxSelectedDate}
              min={minDate}
              isMobile={isMobile}
              onMobileCloseModal={() => setMobileCalendarOpen(false)}
            ></AllieDatetime>
          </StyledMobileModalContent>
        </StyledMobileModalContentContainer>
      </StyledMobileModal>
    </>
  );
};
